import React, { useState } from 'react';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import { useNCZIContext } from '../../../Services/NCZIContext';
import { useEffectAsync } from '../../../../../helpers';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import EHRResult from '../../EHR/EHRResult/EHRResult';
import EHRExtractUnpackCompositionItems from '../../EHR/EHRExtract/EHRExtractUnpackCompositionItems';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { PatientConsentType } from '../../../Commands/WritePatientConsent';
import ElectronicVaccinationFilter from '../Filter/ElectronicVaccinationFilter';
import ElectronicVaccinationCreateModal from '../Create/ElectronicVaccinationCreateModal';
import PatientConsentButton from '../../PatientConsentModal/PatientConsentButton';
import useNCZIElectronicVaccination from '../../../Hooks/useNCZIElectronicVaccination';
import NCZIListPagination from '../../NCZIListPagination/NCZIListPagination';

//@ts-ignore
import styles from './ElectronicVaccinationList.module.scss';

interface Props {
	setIsLoading: (isLoading: boolean) => void;
}

const ElectronicVaccinationList: React.FC<Props> = () => {
	const {
		patientVaccinationData,
		patientVaccinationPage,
		patientIdentifier,
		patientVaccinationFilter,
	} = useNCZIContext();

	const {
		handleLoadPatientVaccinationData,
		handleFakeLoadPatientVaccinationData,
		loadingCommandId,
	} = useNCZIElectronicVaccination();

	const [electronicVaccinationCreateOpen, setElectronicVaccinationCreateOpen] =
		useState<boolean>(false);

	const handleLoadData = async () => {
		if (!patientVaccinationData) {
			await handleLoadPatientVaccinationData(1, patientVaccinationFilter);
			// await handleFakeLoadPatientVaccinationData();
		}
	};

	useEffectAsync(async () => {
		await handleLoadData();
	}, []);

	const handleCloseDetailModal = async (needReload: boolean) => {
		if (needReload) {
			await handleLoadPatientVaccinationData(
				patientVaccinationPage,
				patientVaccinationFilter,
			);
		}
	};

	const handleOpenCreateRecord = () => {
		setElectronicVaccinationCreateOpen(true);
	};

	const handleCloseElectronicVaccinationCreate = async (
		needReload: boolean,
	) => {
		setElectronicVaccinationCreateOpen(false);
		if (needReload) {
			await handleLoadPatientVaccinationData(1, patientVaccinationFilter);
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.filterWrapper}>
				<ElectronicVaccinationFilter
					onFilter={values => handleLoadPatientVaccinationData(1, values)}
				/>
			</div>
			<div className={styles.contentWrapper}>
				<div className={styles.controls}>
					<LinkButton
						title='nczi.vaccination.create.title'
						theme={Theme.BLUE}
						onClick={handleOpenCreateRecord}
						firstUpper
					/>
					<PatientConsentButton
						loadData={() =>
							handleLoadPatientVaccinationData(1, patientVaccinationFilter)
						}
						consentType={PatientConsentType.PSU}
						code={patientVaccinationData?.result?.code}
					/>
				</div>
				<ScrollContainer>
					<div className={styles.content}>
						{!patientVaccinationData || !patientIdentifier ? (
							<div className={styles.loadingWrapper}>
								<NCZICommandResult
									commandId={loadingCommandId}
									handleRetryCommand={() =>
										handleLoadPatientVaccinationData(
											patientVaccinationPage,
											patientVaccinationFilter,
										)
									}
								/>
							</div>
						) : (
							<>
								<EHRResult ehrResult={patientVaccinationData?.result} />
								<EHRExtractUnpackCompositionItems
									ehrExtract={patientVaccinationData?.ehrExtract}
									patientIdentifier={patientIdentifier}
									onCloseDetail={handleCloseDetailModal}
								/>
								<NCZIListPagination
									hasMoreData={
										(patientVaccinationData?.ehrExtract?.items[0]?.items || [])
											.length > 0
									}
									page={patientVaccinationPage ?? 1}
									loadData={page =>
										handleLoadPatientVaccinationData(
											page,
											patientVaccinationFilter,
										)
									}
								/>
							</>
						)}
						<ElectronicVaccinationCreateModal
							opened={electronicVaccinationCreateOpen}
							onClose={handleCloseElectronicVaccinationCreate}
							patientIdentifier={patientIdentifier || ''}
						/>
					</div>
				</ScrollContainer>
			</div>
		</div>
	);
};

export default ElectronicVaccinationList;
